import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Check } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import SortSelect from "../components/SortSelect";
import { sortByOption, DEFAULT_SORT } from "../utils/sort";

import { INCOME_TYPES } from "../utils/expenseTypes";
import { sourceRequired, bankAccountsOf, receivedIntoId } from "../utils/moneySources";
import MoneySourceSelect from "../components/MoneySourceSelect";
import { useMoneySources } from "../components/useMoneySources";

import { API_BASE, apiFetch } from "../api";

// `into` is the "Received into" bank account (see MoneySourceSelect).
const emptyForm = { source: "", type: "Salary", date: "", amount: "", description: "", into: "" };

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const Income = () => {
  const { tokens, fonts } = useTheme();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const sources = useMoneySources();
  const hasAccounts = bankAccountsOf(sources.assets).length > 0;

  const loadIncome = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch(`${API_BASE}/income`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load income");
        return res.json();
      })
      .then(setEntries)
      .catch(() =>
        setError("Couldn't reach the backend. Make sure it's running with `npm run server`.")
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadIncome();
  }, [loadIncome]);

  const filtered = sortByOption(
    entries.filter((item) => filter === "All" || item.status === filter),
    sortBy
  );

  const totals = entries.reduce(
    (acc, item) => {
      if (item.status === "Received") acc.received += item.amount;
      else acc.upcoming += item.amount;
      return acc;
    },
    { received: 0, upcoming: 0 }
  );

  const handleToggleStatus = (item) => {
    const nextStatus = item.status === "Received" ? "Upcoming" : "Received";
    setEntries((prev) => prev.map((e) => (e.id === item.id ? { ...e, status: nextStatus } : e)));
    apiFetch(`${API_BASE}/income/${item.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: nextStatus }),
    }).catch(() => loadIncome());
  };

  const handleDelete = (id) => {
    const previous = entries;
    setEntries((prev) => prev.filter((e) => e.id !== id));
    apiFetch(`${API_BASE}/income/${id}`, { method: "DELETE" }).catch(() => setEntries(previous));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError(null);

    const amount = parseFloat(form.amount);
    if (!form.source.trim()) return setFormError("Source is required.");
    if (!form.date) return setFormError("Date is required.");
    if (Number.isNaN(amount) || amount < 0) return setFormError("Amount must be a valid number.");
    if (!form.into && hasAccounts && sourceRequired(form.date, sources.assets, sources.cards)) return setFormError("Choose the account it goes into.");

    setSaving(true);
    const { into, ...fields } = form;
    apiFetch(`${API_BASE}/income`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...fields, amount, receivedIntoId: receivedIntoId(into) }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((data) => Promise.reject(new Error(data.error)));
        return res.json();
      })
      .then((created) => {
        setEntries((prev) => [...prev, created]);
        setForm(emptyForm);
        setShowForm(false);
      })
      .catch((err) => setFormError(err.message || "Failed to add income."))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ ...styles.page, backgroundColor: tokens.bg, fontFamily: fonts.body }}>
      <h2 style={{ ...styles.heading, fontFamily: fonts.display, color: tokens.textPrimary }}>Income</h2>
      <p style={{ ...styles.subheading, color: tokens.textMuted }}>
        A timeline of cash coming your way — salary, client payments, or money from anyone else.
      </p>

      <div style={styles.statsRow}>
        <div style={{ ...styles.statTile, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
          <span style={{ ...styles.statLabel, color: tokens.textMuted }}>Received</span>
          <span style={{ ...styles.statValue, fontFamily: fonts.mono, color: tokens.positive }}>
            ₹{totals.received.toLocaleString()}
          </span>
        </div>
        <div style={{ ...styles.statTile, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
          <span style={{ ...styles.statLabel, color: tokens.textMuted }}>Upcoming</span>
          <span style={{ ...styles.statValue, fontFamily: fonts.mono, color: tokens.accent }}>
            ₹{totals.upcoming.toLocaleString()}
          </span>
        </div>
      </div>

      <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        {error && (
          <div style={{ ...styles.banner, backgroundColor: tokens.pendingBg, color: tokens.pending }}>{error}</div>
        )}

        <div style={styles.toolbar}>
          {["All", "Upcoming", "Received"].map((btn) => (
            <button
              key={btn}
              onClick={() => setFilter(btn)}
              style={{
                ...styles.filterBtn,
                backgroundColor: filter === btn ? tokens.accent : tokens.neutralSoft,
                color: filter === btn ? tokens.accentContrast : tokens.textPrimary,
              }}
            >
              {btn}
            </button>
          ))}
          <button
            onClick={() => setShowForm((s) => !s)}
            style={{ ...styles.addBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}
          >
            <Plus size={15} strokeWidth={3} />
            Add Income
          </button>
          <SortSelect value={sortBy} onChange={setSortBy} />
        </div>

        {showForm && (
          <form onSubmit={handleFormSubmit} style={{ ...styles.form, border: `1px solid ${tokens.surfaceBorder}` }}>
            <input
              placeholder="Source (e.g. Acme Corp, John)"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            >
              {INCOME_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            {hasAccounts && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary }}>
                Received into
                <MoneySourceSelect
                  style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                  value={form.into}
                  onChange={(v) => setForm({ ...form, into: v })}
                  assets={sources.assets}
                  accountsOnly
                  required={sourceRequired(form.date, sources.assets, sources.cards)}
                />
              </label>
            )}
            <input
              placeholder="Description (optional notes)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface, minWidth: "220px" }}
            />
            <button type="submit" disabled={saving} style={{ ...styles.saveBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
              {saving ? "Saving..." : "Save"}
            </button>
            {formError && <span style={{ ...styles.formError, color: tokens.negative }}>{formError}</span>}
          </form>
        )}

        {loading && <p style={{ color: tokens.textMuted, fontSize: 14 }}>Loading income...</p>}
        {!loading && !error && filtered.length === 0 && (
          <p style={{ color: tokens.textMuted, fontSize: 14 }}>No income entries match this filter.</p>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ position: "relative" }}>
            <div
              style={{
                position: "absolute",
                left: 15,
                top: 8,
                bottom: 8,
                width: 2,
                backgroundColor: tokens.surfaceBorder,
              }}
            />
            {filtered.map((item, i) => {
              const isReceived = item.status === "Received";
              const dotColor = isReceived ? tokens.positive : tokens.accent;
              return (
                <div
                  key={item.id}
                  style={{ display: "flex", gap: 16, paddingBottom: i === filtered.length - 1 ? 0 : 22 }}
                >
                  <div style={{ width: 32, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        marginTop: 3,
                        backgroundColor: dotColor,
                        border: `3px solid ${tokens.surface}`,
                        boxShadow: `0 0 0 2px ${dotColor}`,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      paddingBottom: 14,
                      borderBottom: i === filtered.length - 1 ? "none" : `1px solid ${tokens.surfaceBorder}`,
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: tokens.textMuted }}>
                        {formatDate(item.date)}
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>
                          {item.source}
                        </span>
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: "2px 9px",
                            borderRadius: 999,
                            backgroundColor: tokens.neutralSoft,
                            color: tokens.textMuted,
                          }}
                        >
                          {item.type}
                        </span>
                      </div>
                      {!!item.description && (
                        <span style={{ fontSize: 12.5, color: tokens.textMuted }}>{item.description}</span>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <span style={{ fontFamily: fonts.mono, fontWeight: 700, fontSize: 15, color: dotColor }}>
                        +₹{item.amount.toLocaleString()}
                      </span>
                      <button
                        onClick={() => handleToggleStatus(item)}
                        title={isReceived ? "Mark as upcoming" : "Mark as received"}
                        style={{
                          ...styles.iconBtn,
                          backgroundColor: isReceived ? tokens.positiveBg : tokens.neutralSoft,
                          color: isReceived ? tokens.positive : tokens.textMuted,
                        }}
                      >
                        <Check size={14} strokeWidth={3} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        aria-label={`Delete ${item.source}`}
                        style={{ ...styles.iconBtn, backgroundColor: "transparent", color: tokens.negative }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    padding: "24px",
    minHeight: "100vh",
    transition: "background-color 0.2s ease",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
  },
  subheading: {
    fontSize: "14px",
    margin: "6px 0 20px",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16,
    marginBottom: 20,
    maxWidth: 480,
  },
  statTile: {
    borderRadius: 14,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  statLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  statValue: {
    fontSize: 22,
    fontWeight: 700,
  },
  card: {
    padding: "24px",
    borderRadius: "16px",
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  banner: {
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "13.5px",
    fontWeight: 600,
    marginBottom: "16px",
  },
  toolbar: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  filterBtn: {
    padding: "10px 16px",
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 16px",
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "14px",
    marginLeft: "auto",
  },
  form: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    padding: "16px",
    borderRadius: "12px",
    marginBottom: "20px",
  },
  formInput: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid",
    fontSize: "14px",
    minWidth: "140px",
  },
  saveBtn: {
    padding: "9px 18px",
    border: "none",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
  },
  formError: {
    fontSize: "13px",
    fontWeight: 600,
  },
  iconBtn: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
};

export default Income;
